/**
 * Merkzettel — Sidebar-Pin, schwebendes Fenster am Shell-Host, N Reiter.
 * Klasse MERKZETTEL_SEAT_OR_USER. Schließen nur per X, nicht per Außenklick.
 */

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Bold,
  Highlighter,
  Italic,
  List,
  ListOrdered,
  Loader2,
  Pencil,
  Plus,
  StickyNote,
  Strikethrough,
  Trash2,
  X,
} from 'lucide-react';
import { PORTAL_SHELL } from './portalTheme';
import {
  MERKZETTEL_MARK_ORDER,
  MERKZETTEL_POS_STORAGE_KEY,
  MERKZETTEL_SIZE_DEFAULT,
  MERKZETTEL_SIZE_STORAGE_KEY,
  applyMerkzettelFormat,
  applyMerkzettelTyping,
  buildMerkzettelView,
  canAddMerkzettelTab,
  clipMerkzettelBody,
  clipMerkzettelTitle,
  clipMerkzettelWindowPosition,
  clipMerkzettelWindowSize,
  countMerkzettelLines,
  displayMerkzettelTitle,
  expandMerkzettelSelectionToWord,
  insertMerkzettelLineBreak,
  merkzettelMarkdownFromDomRows,
  mergeSavedMerkzettelTab,
  merkzettelEditorView,
  merkzettelLocateOffset,
  merkzettelPersistHint,
  merkzettelShouldPaintEditor,
  merkzettelTabKey,
  parseMerkzettelWindowPosition,
  parseMerkzettelMarkdown,
  parseMerkzettelWindowSize,
  placeMerkzettelPopover,
  resizeMerkzettelWindow,
  planMerkzettelTabDelete,
  reduceMerkzettelFloat,
  resolveMerkzettelActiveKeyAfterSave,
  selectMerkzettelBackend,
  serializeMerkzettelMarkdown,
  serializeMerkzettelWindowPosition,
  serializeMerkzettelWindowSize,
  type MerkzettelArmedMark,
  type MerkzettelEditorOrigin,
  type MerkzettelFloatState,
  type MerkzettelFormat,
  type MerkzettelMark,
  type MerkzettelRecord,
  type MerkzettelResizeEdge,
  type MerkzettelWindowPos,
  type MerkzettelWindowSize,
} from './portalMerkzettel';
import {
  createMerkzettelTabForSeat,
  loadMerkzettelTabsForSeat,
  removeMerkzettelTabForSeat,
  saveMerkzettelTabForSeat,
} from './portalMerkzettelPersist';

export type MerkzettelDockProps = {
  customerId: string;
  kanzleiId?: string;
  variant?: 'sidebar' | 'rail';
  /** Sidebar- oder Rail-Slot. Das Fenster selbst hängt nicht daran. */
  pinRoot?: HTMLElement | null;
};

const MERKZETTEL_FORMATS: ReadonlyArray<{ id: MerkzettelFormat; label: string; Icon: typeof Bold }> = [
  { id: 'bold', label: 'Fett', Icon: Bold },
  { id: 'italic', label: 'Kursiv', Icon: Italic },
  { id: 'strike', label: 'Durchstreichen', Icon: Strikethrough },
  { id: 'highlight', label: 'Hervorheben', Icon: Highlighter },
  { id: 'bullet', label: 'Aufzählung', Icon: List },
  { id: 'number', label: 'Nummerierung', Icon: ListOrdered },
];

const MERKZETTEL_RESIZE_HANDLES: ReadonlyArray<{
  edge: MerkzettelResizeEdge;
  label: string;
  className: string;
}> = [
  { edge: 'n', label: 'Oben ziehen', className: 'absolute left-4 right-4 top-0 z-20 h-2 cursor-ns-resize border-0 bg-transparent p-0' },
  { edge: 's', label: 'Unten ziehen', className: 'absolute bottom-0 left-4 right-4 z-20 h-2 cursor-ns-resize border-0 bg-transparent p-0' },
  { edge: 'e', label: 'Rechts ziehen', className: 'absolute bottom-4 right-0 top-4 z-20 w-2 cursor-ew-resize border-0 bg-transparent p-0' },
  { edge: 'w', label: 'Links ziehen', className: 'absolute bottom-4 left-0 top-4 z-20 w-2 cursor-ew-resize border-0 bg-transparent p-0' },
  { edge: 'nw', label: 'Ecke oben links', className: 'absolute left-0 top-0 z-20 h-4 w-4 cursor-nwse-resize border-0 bg-transparent p-0' },
  { edge: 'ne', label: 'Ecke oben rechts', className: 'absolute right-0 top-0 z-20 h-4 w-4 cursor-nesw-resize border-0 bg-transparent p-0' },
  { edge: 'sw', label: 'Ecke unten links', className: 'absolute bottom-0 left-0 z-20 h-4 w-4 cursor-nesw-resize border-0 bg-transparent p-0' },
];

const INLINE_PAIR: Record<string, readonly [string, string]> = {
  bold: ['**', '**'],
  italic: ['*', '*'],
  strike: ['~~', '~~'],
  highlight: ['==', '=='],
};

function merkzettelTagKind(el: HTMLElement): string {
  const tagged = el.dataset.mz || '';
  if (tagged) return tagged;
  const tag = el.tagName;
  if (tag === 'STRONG' || tag === 'B') return 'bold';
  if (tag === 'EM' || tag === 'I') return 'italic';
  if (tag === 'S' || tag === 'DEL' || tag === 'STRIKE') return 'strike';
  if (tag === 'MARK') return 'highlight';
  const deco = `${el.style.textDecorationLine || ''} ${el.style.textDecoration || ''}`.toLowerCase();
  if (deco.includes('line-through')) return 'strike';
  if (el.style.backgroundColor) return 'highlight';
  if (el.style.fontStyle === 'italic') return 'italic';
  const weight = el.style.fontWeight;
  if (weight === 'bold' || weight === 'bolder' || Number(weight) >= 600) return 'bold';
  return '';
}

function snapshotMerkzettelEditor(root: HTMLElement): { md: string; start: number; end: number } {
  const sel = typeof window !== 'undefined' ? window.getSelection() : null;
  const anchor = sel?.anchorNode ?? null;
  const focus = sel?.focusNode ?? null;
  const anchorOff = sel?.anchorOffset ?? 0;
  const focusOff = sel?.focusOffset ?? 0;
  const blocks = [...root.querySelectorAll('[data-mz-block]')].filter(
    (el): el is HTMLElement => el instanceof HTMLElement && !el.querySelector('[data-mz-block]'),
  );
  if (!blocks.length) {
    const plain = (root.innerText || '').replace(/\u200b/g, '').replace(/\n$/, '');
    return { md: plain, start: plain.length, end: plain.length };
  }

  const readPiece = (nodes: Node[]) => {
    let seg = '';
    let start: number | null = null;
    let end: number | null = null;
    const mapOffset = (raw: string, domOff: number) => {
      const left = Math.max(0, Math.min(domOff, raw.length));
      let vis = 0;
      for (let i = 0; i < left; i += 1) {
        if (raw[i] !== '\u200b') vis += 1;
      }
      return vis;
    };
    const addVisible = (text: string, node: Node) => {
      const raw = text || '';
      const visible = raw.replace(/\u200b/g, '');
      if (anchor === node) start = seg.length + mapOffset(raw, anchorOff);
      if (focus === node) end = seg.length + mapOffset(raw, focusOff);
      seg += visible;
    };
    const consume = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        addVisible(node.textContent || '', node);
        return;
      }
      if (!(node instanceof HTMLElement)) return;
      if (node.dataset.mzMarker === '1') return;
      if (node.tagName === 'BR') {
        if (anchor === node) start = seg.length;
        if (focus === node) end = seg.length;
        return;
      }
      const kind = merkzettelTagKind(node);
      const pair = kind && kind !== 'text' ? INLINE_PAIR[kind] : undefined;
      if (pair) {
        const before = seg.length;
        seg += pair[0];
        node.childNodes.forEach(consume);
        if (seg.length === before + pair[0].length) seg = seg.slice(0, before);
        else seg += pair[1];
        return;
      }
      node.childNodes.forEach(consume);
    };
    nodes.forEach(consume);
    return { text: seg, start, end };
  };

  const rows: Array<{ kind: 'line' | 'bullet' | 'number'; n?: number; text: string }> = [];
  let anchorAt: { row: number; off: number } | null = null;
  let focusAt: { row: number; off: number } | null = null;
  blocks.forEach((block) => {
    const rawKind = block.dataset.mzBlock || 'line';
    const kind = rawKind === 'bullet' || rawKind === 'number' ? rawKind : 'line';
    const n0 = Number(block.dataset.mzN || '0');
    const hosts = [...block.querySelectorAll(':scope > [data-mz-content]')].filter(
      (el): el is HTMLElement => el instanceof HTMLElement,
    );
    const targets = hosts.length ? hosts : [block];
    let extra = 0;
    targets.forEach((host) => {
      const pieces: Node[][] = [[]];
      host.childNodes.forEach((node) => {
        if (node instanceof HTMLElement && node.tagName === 'BR') {
          pieces.push([]);
          return;
        }
        pieces[pieces.length - 1].push(node);
      });
      const usable =
        pieces.length === 2 && pieces[0].length === 0 && pieces[1].length === 0 ? [[]] : pieces;
      usable.forEach((piece) => {
        const read = readPiece(piece);
        const row = rows.length;
        if (read.start != null) anchorAt = { row, off: read.start };
        if (read.end != null) focusAt = { row, off: read.end };
        rows.push({
          kind,
          n: kind === 'number' ? (n0 > 0 ? n0 : 0) + extra : undefined,
          text: read.text,
        });
        extra += 1;
      });
    });
  });

  const md = merkzettelMarkdownFromDomRows(rows);
  const lines = md.split('\n');
  const abs = (at: { row: number; off: number } | null) => {
    if (!at || at.row < 0 || at.row >= rows.length) return md.length;
    let pos = 0;
    for (let i = 0; i < at.row; i += 1) pos += (lines[i]?.length || 0) + 1;
    const line = lines[at.row] || '';
    const prefix = Math.max(0, line.length - rows[at.row].text.length);
    return pos + prefix + Math.max(0, Math.min(at.off, rows[at.row].text.length));
  };
  let start = abs(anchorAt);
  let end = abs(focusAt);
  if (start > end) {
    const swap = start;
    start = end;
    end = swap;
  }
  return { md, start, end };
}

function merkzettelDomListIsSplit(root: HTMLElement): boolean {
  const blocks = [...root.querySelectorAll('[data-mz-block]')].filter(
    (el): el is HTMLElement => el instanceof HTMLElement && !el.querySelector('[data-mz-block]'),
  );
  const numbers: number[] = [];
  for (const block of blocks) {
    const kind = block.dataset.mzBlock;
    if (kind !== 'number' && kind !== 'bullet') continue;
    if (block.querySelectorAll(':scope > [data-mz-content]').length > 1) return true;
    if (kind === 'number') numbers.push(Number(block.dataset.mzN || '0'));
  }
  if (numbers.some((n) => n < 1)) return true;
  for (let i = 1; i < numbers.length; i += 1) {
    if (numbers[i] !== numbers[i - 1] + 1) return true;
  }
  return false;
}

function placeMerkzettelCaret(root: HTMLElement, raw: string, start: number, end: number): void {
  const point = (index: number) => {
    const loc = merkzettelLocateOffset(raw, index);
    const row = root.querySelector(`[data-mz-b="${loc.block}"]`);
    if (!row) return null;
    if (loc.inline < 0) {
      const content = row.querySelector('[data-mz-content]');
      if (!content) return null;
      return { node: content, offset: 0, text: null as Text | null };
    }
    const span = row.querySelector(`[data-mz-i="${loc.inline}"]`);
    if (!span) return null;
    const walker = document.createTreeWalker(span, NodeFilter.SHOW_TEXT);
    let text = walker.nextNode() as Text | null;
    if (!text) {
      text = document.createTextNode('');
      let host: Node = span;
      while (host.firstChild && host.firstChild.nodeType === Node.ELEMENT_NODE) host = host.firstChild;
      host.appendChild(text);
    }
    return { node: text, offset: Math.max(0, Math.min(loc.char, text.length)), text };
  };
  const from = point(start);
  const to = point(end);
  const sel = window.getSelection();
  if (!from || !to || !sel) return;
  const range = document.createRange();
  if (from.text) range.setStart(from.text, from.offset);
  else range.setStart(from.node, 0);
  if (to.text) range.setEnd(to.text, to.offset);
  else range.setEnd(to.node, 0);
  sel.removeAllRanges();
  sel.addRange(range);
}

function runElement(kind: string): HTMLElement {
  if (kind === 'bold') return document.createElement('strong');
  if (kind === 'italic') return document.createElement('em');
  if (kind === 'strike') return document.createElement('s');
  if (kind === 'highlight') return document.createElement('mark');
  return document.createElement('span');
}

function styleMerkzettelMark(el: HTMLElement, mark: string): void {
  if (mark === 'bold') el.className = 'font-bold';
  else if (mark === 'italic') el.className = 'italic';
  else if (mark === 'strike') {
    el.className = 'line-through decoration-slate-400';
    el.style.textDecoration = 'line-through';
  } else if (mark === 'highlight') {
    el.className = 'rounded-sm px-0.5';
    el.style.backgroundColor = 'rgba(232, 210, 150, 0.28)';
    el.style.color = 'inherit';
  }
}

/**
 * Der Editor besitzt den Knoten. textContent, kein innerHTML aus dem Zettel.
 * React setzt hier keine Kinder — sonst removeChild beim Tippen.
 */
function paintMerkzettelEditor(root: HTMLElement, body: string, armed: MerkzettelArmedMark | null): void {
  const blocks = merkzettelEditorView(body, armed);
  const frag = document.createDocumentFragment();
  const paintInline = (content: HTMLElement, inlines: (typeof blocks)[number]['inlines']) => {
    if (!inlines.length) {
      content.appendChild(document.createElement('br'));
      return;
    }
    inlines.forEach((span, inlineIndex) => {
      const marks = span.marks?.length
        ? MERKZETTEL_MARK_ORDER.filter((mark) => span.marks.includes(mark))
        : span.kind !== 'text'
          ? [span.kind]
          : [];
      const isArmed = Boolean(
        armed && span.kind === armed.kind && span.text === '' && span.mdStart === armed.at,
      );
      const host = document.createElement('span');
      host.dataset.mzI = String(inlineIndex);
      if (isArmed) host.dataset.mzArmed = '1';
      let node: HTMLElement = host;
      for (const mark of marks) {
        const inner = runElement(mark);
        inner.dataset.mz = mark;
        styleMerkzettelMark(inner, mark);
        node.appendChild(inner);
        node = inner;
      }
      const shown = span.text.replace(/\u200b/g, '');
      node.textContent = shown || (isArmed ? '\u200b' : '');
      content.appendChild(host);
    });
  };
  let index = 0;
  while (index < blocks.length) {
    const kind = blocks[index].kind;
    if (kind === 'bullet' || kind === 'number') {
      const list = document.createElement(kind === 'number' ? 'ol' : 'ul');
      list.dataset.mzList = kind;
      list.style.listStyleType = kind === 'number' ? 'decimal' : 'disc';
      list.style.margin = '0';
      list.style.paddingLeft = '1.25rem';
      while (index < blocks.length && blocks[index].kind === kind) {
        const block = blocks[index];
        const row = document.createElement('li');
        row.dataset.mzBlock = block.kind;
        row.dataset.mzB = String(index);
        if (block.kind === 'number') row.dataset.mzN = String(block.n ?? index + 1);
        row.className = 'min-h-5';
        const content = document.createElement('span');
        content.dataset.mzContent = '1';
        content.className = 'whitespace-pre-wrap break-words';
        paintInline(content, block.inlines);
        row.appendChild(content);
        list.appendChild(row);
        index += 1;
      }
      frag.appendChild(list);
    } else {
      const block = blocks[index];
      const row = document.createElement('div');
      row.dataset.mzBlock = 'line';
      row.dataset.mzB = String(index);
      row.className = 'min-h-5';
      const content = document.createElement('span');
      content.dataset.mzContent = '1';
      content.className = 'whitespace-pre-wrap break-words';
      paintInline(content, block.inlines);
      row.appendChild(content);
      frag.appendChild(row);
      index += 1;
    }
  }
  root.replaceChildren(frag);
}

function readStoredSize(): MerkzettelWindowSize {
  if (typeof window === 'undefined') return MERKZETTEL_SIZE_DEFAULT;
  try {
    const parsed = parseMerkzettelWindowSize(window.localStorage.getItem(MERKZETTEL_SIZE_STORAGE_KEY));
    return parsed || MERKZETTEL_SIZE_DEFAULT;
  } catch {
    return MERKZETTEL_SIZE_DEFAULT;
  }
}

function writeStoredSize(size: MerkzettelWindowSize): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(MERKZETTEL_SIZE_STORAGE_KEY, serializeMerkzettelWindowSize(size));
  } catch {
    /* quota / private mode */
  }
}

function readStoredPos(): MerkzettelWindowPos | null {
  if (typeof window === 'undefined') return null;
  try {
    return parseMerkzettelWindowPosition(window.localStorage.getItem(MERKZETTEL_POS_STORAGE_KEY));
  } catch {
    return null;
  }
}

function writeStoredPos(pos: MerkzettelWindowPos): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(MERKZETTEL_POS_STORAGE_KEY, serializeMerkzettelWindowPosition(pos));
  } catch {
    /* quota / private mode */
  }
}

function viewportSize(): { width: number; height: number } {
  if (typeof window === 'undefined') return { width: 1280, height: 800 };
  return { width: window.innerWidth, height: window.innerHeight };
}

export const MerkzettelDock: React.FC<MerkzettelDockProps> = ({
  customerId,
  kanzleiId,
  variant = 'sidebar',
  pinRoot = null,
}) => {
  const backend = selectMerkzettelBackend();
  const persistHint = merkzettelPersistHint(backend);
  const [tabs, setTabs] = useState<MerkzettelRecord[]>([]);
  const [activeKey, setActiveKey] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [pendingDeleteKey, setPendingDeleteKey] = useState<string | null>(null);
  const [armed, setArmed] = useState<MerkzettelArmedMark | null>(null);
  const [editorFocused, setEditorFocused] = useState(false);
  const [size, setSize] = useState<MerkzettelWindowSize>(readStoredSize);
  const [float, setFloat] = useState<MerkzettelFloatState>(() => {
    const stored = readStoredPos();
    return { open: false, left: stored?.left ?? 24, top: stored?.top ?? 96 };
  });
  const open = float.open;
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const caretRef = useRef<{ start: number; end: number } | null>(null);
  const editorOwnsDomRef = useRef(false);
  const breakLineRef = useRef<() => void>(() => {});
  const lineBreakGuard = useRef(false);
  const placeArmedRef = useRef(false);
  const composingRef = useRef(false);
  const gutterRef = useRef<HTMLDivElement>(null);
  const saveTimer = useRef<number | null>(null);
  const flushTimer = useRef<number | null>(null);
  const flushActiveRef = useRef<(origin: MerkzettelEditorOrigin) => void>(() => {});
  const tabsRef = useRef(tabs);
  const activeKeyRef = useRef(activeKey);
  const pendingDeleteRef = useRef(pendingDeleteKey);
  tabsRef.current = tabs;
  activeKeyRef.current = activeKey;
  pendingDeleteRef.current = pendingDeleteKey;

  const activeIndex = Math.max(
    0,
    tabs.findIndex((row, i) => merkzettelTabKey(row, i) === activeKey),
  );
  const active = tabs[activeIndex] || tabs[0];
  const lineCount = Math.max(
    1,
    buildMerkzettelView(active?.body || '').length || countMerkzettelLines(active?.body || ''),
  );
  const lineNumbers = useMemo(
    () => Array.from({ length: lineCount }, (_, i) => i + 1),
    [lineCount],
  );

  const applyTabs = useCallback((next: MerkzettelRecord[], preferKey?: string) => {
    setTabs(next);
    const keys = next.map((row, i) => merkzettelTabKey(row, i));
    const pick =
      (preferKey && keys.includes(preferKey) ? preferKey : null) ||
      (activeKeyRef.current && keys.includes(activeKeyRef.current) ? activeKeyRef.current : keys[0] || '');
    setActiveKey(pick);
  }, []);

  const persistTab = useCallback(async (keyToSave: string) => {
    if (keyToSave && keyToSave === activeKeyRef.current) flushActiveRef.current('input');
    const list = tabsRef.current;
    const idx = list.findIndex((row, i) => merkzettelTabKey(row, i) === keyToSave);
    const row = idx >= 0 ? list[idx] : undefined;
    if (!row) {
      setError('Kein Merkzettel geladen — Speichern abgebrochen.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const saved = await saveMerkzettelTabForSeat({
        customerId,
        kanzleiId,
        recordId: row.id,
        title: row.title,
        body: row.body,
        sort: row.sort,
      });
      const savedKey = saved.id ? merkzettelTabKey(saved, idx) : keyToSave;
      const next = mergeSavedMerkzettelTab(tabsRef.current, saved, keyToSave);
      const nextActive = resolveMerkzettelActiveKeyAfterSave({
        tabs: next,
        activeKeyBeforeSave: keyToSave,
        activeKeyNow: activeKeyRef.current,
        savedTabKey: savedKey,
      });
      setTabs(next);
      setActiveKey(nextActive);
      setStatus(`Gespeichert · ${persistHint}`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  }, [customerId, kanzleiId, persistHint]);

  const persistActive = useCallback(async () => {
    await persistTab(activeKeyRef.current);
  }, [persistTab]);

  const selectMerkzettelTab = useCallback((nextKey: string) => {
    const prev = activeKeyRef.current;
    if (!nextKey || nextKey === prev) return;
    flushActiveRef.current('external');
    editorOwnsDomRef.current = false;
    setArmed(null);
    caretRef.current = null;
    setActiveKey(nextKey);
    setPendingDeleteKey(null);
    if (prev) void persistTab(prev);
  }, [persistTab]);

  const close = useCallback(() => {
    if (pendingDeleteRef.current) {
      setPendingDeleteKey(null);
      return;
    }
    setFloat((current) => reduceMerkzettelFloat(current, { type: 'close' }));
    void persistActive();
  }, [persistActive]);

  const placeOpen = useCallback(() => {
    const vp = viewportSize();
    const nextSize = clipMerkzettelWindowSize(size, vp);
    const stored = readStoredPos();
    const placed = stored
      ? clipMerkzettelWindowPosition(stored, nextSize, vp)
      : triggerRef.current
        ? placeMerkzettelPopover(triggerRef.current.getBoundingClientRect(), vp, nextSize)
        : clipMerkzettelWindowPosition({ left: 24, top: 96 }, nextSize, vp);
    writeStoredPos(placed);
    setFloat((current) => reduceMerkzettelFloat(current, { type: 'open', left: placed.left, top: placed.top }));
  }, [size]);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    setError('');
    void loadMerkzettelTabsForSeat(customerId)
      .then((rows) => {
        if (cancelled) return;
        applyTabs(rows);
        setLoaded(true);
        setStatus(persistHint);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoaded(true);
        setError(err instanceof Error ? err.message : 'Merkzettel nicht geladen.');
      });
    return () => {
      cancelled = true;
    };
  }, [applyTabs, customerId, persistHint]);

  useEffect(() => {
    if (!open || !loaded) return undefined;
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      void persistActive();
    }, 900);
    return () => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
    };
  }, [active?.body, active?.title, open, loaded, persistActive]);

  useEffect(() => {
    if (!open || typeof window === 'undefined') return undefined;
    const onResize = () => {
      const vp = viewportSize();
      setSize((current) => {
        const next = clipMerkzettelWindowSize(current, vp);
        if (next.width === current.width && next.height === current.height) return current;
        writeStoredSize(next);
        return next;
      });
      setFloat((current) => {
        const next = clipMerkzettelWindowPosition(current, size, vp);
        if (next.left === current.left && next.top === current.top) return current;
        writeStoredPos(next);
        return reduceMerkzettelFloat(current, { type: 'move', left: next.left, top: next.top });
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open, size]);

  const patchActive = (partial: Partial<Pick<MerkzettelRecord, 'title' | 'body'>>) => {
    setTabs((prev) => {
      const idx = prev.findIndex((row, i) => merkzettelTabKey(row, i) === activeKey);
      if (idx < 0) return prev;
      if (partial.body !== undefined) {
        return applyMerkzettelTyping(prev, idx, partial.body);
      }
      return prev.map((row, i) =>
        i === idx
          ? {
              ...row,
              title: partial.title !== undefined ? clipMerkzettelTitle(partial.title, { trim: false }) : row.title,
            }
          : row,
      );
    });
  };

  const addTab = async () => {
    if (!canAddMerkzettelTab(tabs)) return;
    setBusy(true);
    setError('');
    try {
      await persistActive();
      const created = await createMerkzettelTabForSeat({ customerId, kanzleiId });
      const rows = await loadMerkzettelTabsForSeat(customerId);
      applyTabs(rows, created.id);
      setStatus(`Gespeichert · ${persistHint}`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Reiter nicht angelegt.');
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDeleteKey) return;
    const idx = tabs.findIndex((row, i) => merkzettelTabKey(row, i) === pendingDeleteKey);
    const row = tabs[idx];
    setBusy(true);
    setError('');
    try {
      const result = await removeMerkzettelTabForSeat({
        customerId,
        recordId: row?.id,
      });
      applyTabs(result.tabs);
      setPendingDeleteKey(null);
      setStatus(result.mode === 'cleared' ? `Reiter geleert · ${persistHint}` : `Reiter gelöscht · ${persistHint}`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Löschen fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  const onEditorScroll = () => {
    if (gutterRef.current && editorRef.current) {
      gutterRef.current.scrollTop = editorRef.current.scrollTop;
    }
  };

  const commitEditor = (
    md: string,
    start: number,
    end: number,
    origin: MerkzettelEditorOrigin,
    continueKind: MerkzettelArmedMark['kind'] | null = null,
    continueMarks: MerkzettelMark[] | null = null,
  ) => {
    const safe = serializeMerkzettelMarkdown(parseMerkzettelMarkdown(clipMerkzettelBody(md)));
    const list = tabsRef.current;
    const idx = list.findIndex((row, i) => merkzettelTabKey(row, i) === activeKeyRef.current);
    const current = idx >= 0 ? list[idx].body : '';
    const root = editorRef.current;
    const focused = Boolean(root && document.activeElement === root);
    const paint = merkzettelShouldPaintEditor({ focused, origin });
    const caret = { start: Math.min(start, safe.length), end: Math.min(end, safe.length) };
    const marks = continueMarks?.length ? continueMarks : continueKind ? [continueKind] : [];
    const arm: MerkzettelArmedMark | null = marks.length
      ? { kind: continueKind || marks[0], marks, at: caret.start }
      : null;
    if (origin !== 'input') {
      placeArmedRef.current = Boolean(arm);
      setArmed(arm);
    }
    if (safe === current) {
      editorOwnsDomRef.current = origin === 'input';
      if (origin === 'structure' && root && paint) {
        caretRef.current = arm ? null : caret;
        paintMerkzettelEditor(root, safe, arm);
        if (arm) {
          const el = root.querySelector('[data-mz-armed="1"]');
          if (el) {
            const range = document.createRange();
            range.selectNodeContents(el);
            range.collapse(false);
            const sel = window.getSelection();
            sel?.removeAllRanges();
            sel?.addRange(range);
            root.focus();
          }
        } else {
          placeMerkzettelCaret(root, safe, caret.start, caret.end);
        }
        caretRef.current = null;
      }
      return;
    }
    editorOwnsDomRef.current = !paint;
    caretRef.current = arm ? null : caret;
    if (origin === 'structure' && root && paint) {
      paintMerkzettelEditor(root, safe, arm);
      if (arm) {
        const el = root.querySelector('[data-mz-armed="1"]');
        if (el) {
          const range = document.createRange();
          range.selectNodeContents(el);
          range.collapse(false);
          const sel = window.getSelection();
          sel?.removeAllRanges();
          sel?.addRange(range);
          root.focus();
        }
      } else {
        placeMerkzettelCaret(root, safe, caret.start, caret.end);
      }
    }
    if (idx >= 0) {
      const next = applyMerkzettelTyping(list, idx, safe);
      tabsRef.current = next;
      setTabs(next);
    }
  };

  const flushEditor = (origin: MerkzettelEditorOrigin) => {
    const root = editorRef.current;
    if (!root) return;
    const snap = snapshotMerkzettelEditor(root);
    commitEditor(snap.md, snap.start, snap.end, origin, null);
  };
  flushActiveRef.current = flushEditor;

  const scheduleFlush = () => {
    if (flushTimer.current) window.clearTimeout(flushTimer.current);
    flushTimer.current = window.setTimeout(() => {
      flushTimer.current = null;
      const root = editorRef.current;
      const focused = Boolean(root && document.activeElement === root);
      flushEditor(focused ? 'input' : 'external');
    }, 400);
  };

  const onEditorInput = () => {
    if (composingRef.current) return;
    const root = editorRef.current;
    if (root && merkzettelDomListIsSplit(root)) {
      if (flushTimer.current) {
        window.clearTimeout(flushTimer.current);
        flushTimer.current = null;
      }
      flushEditor('structure');
      return;
    }
    editorOwnsDomRef.current = true;
    scheduleFlush();
  };

  const onEditorKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const root = editorRef.current;
    if (!root) return;
    if (event.key === 'Enter') {
      event.preventDefault();
      breakLineRef.current();
      return;
    }
    if (event.key === 'Backspace') {
      const snap = snapshotMerkzettelEditor(root);
      if (snap.start !== snap.end || snap.start === 0 || snap.md[snap.start - 1] !== '\n') return;
      event.preventDefault();
      const next = snap.md.slice(0, snap.start - 1) + snap.md.slice(snap.start);
      commitEditor(next, snap.start - 1, snap.start - 1, 'structure');
    }
  };

  const breakMerkzettelLine = () => {
    if (lineBreakGuard.current) return;
    lineBreakGuard.current = true;
    window.setTimeout(() => {
      lineBreakGuard.current = false;
    }, 0);
    const root = editorRef.current;
    if (!root) return;
    if (flushTimer.current) {
      window.clearTimeout(flushTimer.current);
      flushTimer.current = null;
    }
    const snap = snapshotMerkzettelEditor(root);
    const collapsed =
      snap.start === snap.end ? snap.md : snap.md.slice(0, snap.start) + snap.md.slice(snap.end);
    const at = Math.min(snap.start, collapsed.length);
    const next = insertMerkzettelLineBreak(collapsed, at, armed?.kind ?? null);
    commitEditor(next.body, next.caret, next.caret, 'structure', next.continueKind, next.continueMarks);
  };
  breakLineRef.current = breakMerkzettelLine;

  const onEditorPaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault();
    const root = editorRef.current;
    if (!root) return;
    const text = event.clipboardData.getData('text/plain').replace(/\r\n/g, '\n');
    const snap = snapshotMerkzettelEditor(root);
    const next = snap.md.slice(0, snap.start) + text + snap.md.slice(snap.end);
    commitEditor(next, snap.start + text.length, snap.start + text.length, 'structure');
  };

  const applyFormat = (format: MerkzettelFormat) => {
    const root = editorRef.current;
    if (!root) return;
    const snap = snapshotMerkzettelEditor(root);
    root.focus();
    editorOwnsDomRef.current = false;
    const source = snap.md;
    if (format === 'bullet' || format === 'number') {
      const next = applyMerkzettelFormat(source, snap.start, snap.end, format);
      caretRef.current = { start: next.start, end: next.end };
      placeArmedRef.current = false;
      setArmed(null);
      paintMerkzettelEditor(root, next.body, null);
      placeMerkzettelCaret(root, next.body, next.start, next.end);
      patchActive({ body: next.body });
      return;
    }
    const [wordStart, wordEnd] = expandMerkzettelSelectionToWord(source, snap.start, snap.end);
    if (wordStart !== wordEnd) {
      const next = applyMerkzettelFormat(source, wordStart, wordEnd, format);
      caretRef.current = { start: next.start, end: next.end };
      placeArmedRef.current = false;
      setArmed(null);
      paintMerkzettelEditor(root, next.body, null);
      placeMerkzettelCaret(root, next.body, next.start, next.end);
      patchActive({ body: next.body });
      return;
    }
    if (armed && armed.kind === format && armed.at === snap.start) {
      setArmed(null);
      return;
    }
    setArmed({ kind: format, at: snap.start });
    placeArmedRef.current = true;
    if (format === 'bold' || format === 'italic' || format === 'strike' || format === 'highlight') {
      paintMerkzettelEditor(root, source, { kind: format, at: snap.start });
      const el = root.querySelector('[data-mz-armed="1"]');
      if (el) {
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
        root.focus();
      }
    }
  };

  useLayoutEffect(() => {
    if (!open) return;
    const root = editorRef.current;
    if (!root) return;
    const focused = document.activeElement === root;
    const owns = editorOwnsDomRef.current;
    editorOwnsDomRef.current = false;
    const want = active?.body || '';
    if (owns && !merkzettelShouldPaintEditor({ focused, origin: 'input' })) {
      if (snapshotMerkzettelEditor(root).md === want) {
        caretRef.current = null;
        return;
      }
    }
    paintMerkzettelEditor(root, want, armed);
    if (armed && placeArmedRef.current) {
      const el = root.querySelector('[data-mz-armed="1"]');
      placeArmedRef.current = false;
      if (!el) return;
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
      root.focus();
      return;
    }
    const caret = caretRef.current;
    if (caret) {
      root.focus();
      placeMerkzettelCaret(root, want, caret.start, caret.end);
      caretRef.current = null;
      return;
    }
    if (document.activeElement === root) {
      const end = want.length;
      placeMerkzettelCaret(root, want, end, end);
    }
  }, [open, active?.body, activeKey, armed]);

  useEffect(() => {
    if (!open) return undefined;
    const root = editorRef.current;
    if (!root) return undefined;
    const onBefore = (event: InputEvent) => {
      if (event.isComposing) return;
      if (event.inputType !== 'insertParagraph' && event.inputType !== 'insertLineBreak') return;
      event.preventDefault();
      breakLineRef.current();
    };
    root.addEventListener('beforeinput', onBefore);
    return () => root.removeEventListener('beforeinput', onBefore);
  }, [open]);

  const onDragPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest('button, a, input, textarea')) return;
    event.preventDefault();
    const pointerId = event.pointerId;
    const originX = event.clientX;
    const originY = event.clientY;
    const originLeft = float.left;
    const originTop = float.top;
    const onMove = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      const next = clipMerkzettelWindowPosition(
        {
          left: originLeft + (ev.clientX - originX),
          top: originTop + (ev.clientY - originY),
        },
        size,
        viewportSize(),
      );
      setFloat((current) => reduceMerkzettelFloat(current, { type: 'move', left: next.left, top: next.top }));
    };
    const onUp = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      setFloat((current) => {
        writeStoredPos({ left: current.left, top: current.top });
        return current;
      });
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  const onResizePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const handle = event.currentTarget;
    const edge = (handle.dataset.edge || 'se') as MerkzettelResizeEdge;
    const pointerId = event.pointerId;
    const startX = event.clientX;
    const startY = event.clientY;
    const startW = size.width;
    const startH = size.height;
    const startLeft = float.left;
    const startTop = float.top;
    try {
      handle.setPointerCapture(pointerId);
    } catch {
      /* Capture optional — Listener hängen an window, nicht am Handle. */
    }
    const onMove = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId || typeof window === 'undefined') return;
      const next = resizeMerkzettelWindow({
        left: startLeft,
        top: startTop,
        width: startW,
        height: startH,
        dx: ev.clientX - startX,
        dy: ev.clientY - startY,
        edge,
        viewport: { width: window.innerWidth, height: window.innerHeight },
      });
      setSize({ width: next.width, height: next.height });
      setFloat((current) => reduceMerkzettelFloat(current, { type: 'move', left: next.left, top: next.top }));
    };
    const onUp = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      try {
        if (handle.hasPointerCapture(pointerId)) handle.releasePointerCapture(pointerId);
      } catch {
        /* bereits frei */
      }
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      setSize((current) => {
        writeStoredSize(current);
        return current;
      });
      setFloat((current) => {
        writeStoredPos({ left: current.left, top: current.top });
        return current;
      });
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  const deleteMode = planMerkzettelTabDelete(tabs.length);
  const isSidebar = variant === 'sidebar';

  const trigger = (
    <button
      ref={triggerRef}
      type="button"
      data-testid="portal-merkzettel-toggle"
      aria-expanded={open}
      aria-haspopup="dialog"
      aria-label={open ? 'Merkzettel zuklappen' : 'Merkzettel öffnen'}
      title="Merkzettel"
      onClick={() => {
        if (open) close();
        else placeOpen();
      }}
      className={
        isSidebar
          ? `${PORTAL_SHELL.sidebarNavItem} ${open ? PORTAL_SHELL.sidebarNavActive : PORTAL_SHELL.sidebarNavIdle}`
          : 'inline-flex h-11 w-11 items-center justify-center rounded-lg border border-amber-400/30 bg-slate-950 text-amber-200 shadow-lg hover:bg-slate-900'
      }
    >
      <StickyNote className="h-4 w-4 flex-shrink-0 opacity-80" aria-hidden />
      {isSidebar ? <span className="truncate">Merkzettel</span> : null}
    </button>
  );

  const popover =
    open && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={rootRef}
            role="dialog"
            aria-label="Merkzettel"
            data-testid="portal-merkzettel-popover"
            data-portal-merkzettel="1"
            data-merkzettel-dismiss="x-only"
            className="pointer-events-auto fixed z-[55] flex flex-col overflow-hidden rounded-xl border border-white/[0.18] bg-[#14161c] shadow-[0_22px_50px_rgba(0,0,0,0.55)] animate-fade-in"
            style={{
              left: float.left,
              top: float.top,
              width: size.width,
              height: size.height,
            }}
          >
            <div
              data-testid="portal-merkzettel-drag"
              onPointerDown={onDragPointerDown}
              className="flex cursor-grab items-center gap-1 border-b border-white/[0.06] px-2 py-1.5 active:cursor-grabbing"
            >
              <p className="min-w-0 flex-1 truncate text-sm font-medium text-white">Merkzettel</p>
              <button
                type="button"
                data-testid="portal-merkzettel-close"
                onClick={close}
                className="rounded-lg p-1 text-slate-400 hover:bg-white/[0.06] hover:text-white"
                aria-label="Merkzettel zuklappen"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex items-center gap-1 border-b border-white/[0.06] px-2 py-1 overflow-x-auto custom-scrollbar">
              {tabs.map((row, i) => {
                const key = merkzettelTabKey(row, i);
                const selected = key === activeKey;
                return selected ? (
                  <label
                    key={key}
                    className="flex h-7 min-w-[6.5rem] max-w-[11rem] flex-shrink-0 items-center gap-1 rounded-md border border-white/25 bg-white/[0.08] px-1.5"
                  >
                    <Pencil className="h-3 w-3 flex-shrink-0 text-slate-400" aria-hidden />
                    <input
                      data-testid="portal-merkzettel-tab-title"
                      data-tab-index={String(i)}
                      value={row.title}
                      onChange={(e) => patchActive({ title: e.target.value })}
                      placeholder={displayMerkzettelTitle('', i)}
                      maxLength={80}
                      className="h-6 min-w-0 flex-1 bg-transparent text-[11px] text-white placeholder:text-slate-500 focus:outline-none"
                      aria-label="Zettel umbenennen"
                      title="Zettel umbenennen"
                    />
                  </label>
                ) : (
                  <button
                    key={key}
                    type="button"
                    data-testid="portal-merkzettel-tab"
                    data-tab-index={String(i)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      selectMerkzettelTab(key);
                    }}
                    onClick={() => selectMerkzettelTab(key)}
                    className="h-7 max-w-[8rem] flex-shrink-0 truncate rounded-md px-2 text-[11px] text-slate-400 hover:bg-white/[0.06] hover:text-white"
                  >
                    {displayMerkzettelTitle(row.title, i)}
                  </button>
                );
              })}
              <button
                type="button"
                data-testid="portal-merkzettel-tab-add"
                onClick={() => void addTab()}
                disabled={busy || !loaded || !canAddMerkzettelTab(tabs)}
                className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-slate-400 hover:bg-white/[0.06] hover:text-white disabled:opacity-40"
                aria-label="Reiter hinzufügen"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                data-testid="portal-merkzettel-tab-delete"
                onClick={() => setPendingDeleteKey(activeKey)}
                disabled={busy || !loaded || !active}
                className="ml-auto inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-slate-500 hover:bg-white/[0.06] hover:text-red-300 disabled:opacity-40"
                aria-label="Reiter löschen"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>

            <div
              role="toolbar"
              aria-label="Format"
              data-testid="portal-merkzettel-format"
              className="flex items-center gap-0.5 border-b border-white/[0.06] px-2 py-1"
            >
              {MERKZETTEL_FORMATS.map(({ id, label, Icon }) => (
                <button
                  key={id}
                  type="button"
                  data-testid={`portal-merkzettel-format-${id}`}
                  aria-label={label}
                  title={label}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => applyFormat(id)}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-400 hover:bg-white/[0.06] hover:text-white"
                >
                  <Icon className="h-3.5 w-3.5" aria-hidden />
                </button>
              ))}
            </div>

            <label className="sr-only" htmlFor="portal-merkzettel-body">
              Notiz
            </label>
            <div
              className="relative min-h-0 flex-1 bg-[#1b1e26]"
              data-testid="portal-merkzettel-editor"
              onMouseDown={(e) => {
                if ((e.target as HTMLElement).closest('button')) return;
                if (document.activeElement !== editorRef.current) editorRef.current?.focus();
              }}
            >
              {!active?.body && !armed && !editorFocused ? (
                <div className="pointer-events-none absolute inset-0 z-0 py-2 pl-9 pr-2 text-[13px] leading-5 text-slate-500">
                  Schnell notieren — bleibt an diesem Sitz.
                </div>
              ) : null}
              <div
                ref={editorRef}
                id="portal-merkzettel-body"
                data-testid="portal-merkzettel-body"
                data-markdown={active?.body ?? ''}
                contentEditable
                suppressContentEditableWarning
                role="textbox"
                aria-multiline="true"
                aria-label="Notiz"
                spellCheck
                autoFocus
                onInput={onEditorInput}
                onKeyDown={onEditorKeyDown}
                onPaste={onEditorPaste}
                onScroll={onEditorScroll}
                onFocus={() => setEditorFocused(true)}
                onCompositionStart={() => {
                  composingRef.current = true;
                }}
                onCompositionEnd={() => {
                  composingRef.current = false;
                  onEditorInput();
                }}
                onBlur={() => {
                  setEditorFocused(false);
                  if (flushTimer.current) {
                    window.clearTimeout(flushTimer.current);
                    flushTimer.current = null;
                  }
                  flushEditor('external');
                }}
                className="absolute inset-0 z-[1] overflow-auto bg-transparent py-2 pl-9 pr-2 text-[13px] leading-5 text-[#e6e7ea] focus:outline-none"
              />
              <div
                ref={gutterRef}
                data-testid="portal-merkzettel-gutter"
                aria-hidden
                className="pointer-events-none absolute inset-y-0 left-0 z-[2] w-8 overflow-hidden border-r border-white/[0.12] bg-[#1b1e26] py-2 text-right font-mono text-[13px] leading-5 text-slate-500 select-none"
              >
                {lineNumbers.map((n) => (
                  <div key={n} className="pr-1.5">
                    {n}
                  </div>
                ))}
              </div>
            </div>

            {pendingDeleteKey ? (
              <div
                role="alertdialog"
                aria-label="Reiter löschen"
                data-testid="portal-merkzettel-delete-confirm"
                className="border-t border-white/[0.06] px-3 py-2"
              >
                <p className="text-[12px] text-slate-300">
                  {deleteMode === 'clear-last'
                    ? 'Letzten Reiter leeren? Ein leerer Zettel bleibt.'
                    : 'Diesen Reiter löschen?'}
                </p>
                <div className="mt-2 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setPendingDeleteKey(null)}
                    className="rounded-lg px-2 py-1 text-[11px] text-slate-300 hover:bg-white/[0.06]"
                  >
                    Abbrechen
                  </button>
                  <button
                    type="button"
                    data-testid="portal-merkzettel-delete-yes"
                    onClick={() => void confirmDelete()}
                    className="rounded-lg border border-red-400/30 px-2 py-1 text-[11px] text-red-200 hover:bg-red-500/10"
                  >
                    {deleteMode === 'clear-last' ? 'Leeren' : 'Löschen'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-2 border-t border-white/[0.06] px-3 py-2">
                <p
                  className="min-w-0 truncate text-[11px] text-slate-500"
                  role={error ? 'alert' : undefined}
                  title={error || undefined}
                >
                  {error || status || (loaded ? persistHint : 'Lädt …')}
                </p>
                <button
                  type="button"
                  onClick={() => void persistActive()}
                  disabled={busy || !loaded || !active}
                  data-testid="portal-merkzettel-save"
                  className="inline-flex items-center gap-1 rounded-lg border border-white/[0.08] px-2 py-1 text-[11px] text-slate-200 hover:bg-white/[0.06] disabled:opacity-50"
                >
                  {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                  Speichern
                </button>
              </div>
            )}

            {MERKZETTEL_RESIZE_HANDLES.map(({ edge, label, className }) => (
              <button
                key={edge}
                type="button"
                data-testid={`portal-merkzettel-resize-${edge}`}
                data-edge={edge}
                aria-label={label}
                onPointerDown={onResizePointerDown}
                className={className}
              />
            ))}
            <button
              type="button"
              data-testid="portal-merkzettel-resize"
              data-edge="se"
              aria-label="Größe ändern"
              onPointerDown={onResizePointerDown}
              style={{ zIndex: 30 }}
              className="absolute bottom-0 right-0 h-4 w-4 cursor-nwse-resize border-0 bg-transparent p-0"
            >
              <span className="absolute bottom-1 right-1 h-2 w-2 border-b border-r border-white/30" aria-hidden />
            </button>
          </div>,
          document.body,
        )
      : null;

  const triggerNode = pinRoot ? createPortal(trigger, pinRoot) : trigger;

  return (
    <div data-testid="portal-merkzettel-dock" data-portal-merkzettel-variant={variant}>
      {triggerNode}
      {popover}
    </div>
  );
};
